<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">{{ note ? 'Edit Note' : 'Create Note' }}</h1>
      <p class="subtitle">{{ note ? 'Update the details of this patient note.' : 'Add a new note for this patient.' }}</p>
    </div>
    <div class="modal-card-body">
      <div class="form-grid">
        <div class="form-row">
          <label class="form-label">Type</label>
          <SelectInput v-model="type" :options="typeOptions" placeholder="Select type..." />
        </div>

        <div v-if="type === 'product_log'" class="form-row">
          <label class="form-label">Product</label>
          <ProductSelectInput v-model="productId" />
        </div>

        <div class="form-row">
          <label class="form-label label-required">Description</label>
          <TextareaInput v-model="description" placeholder="Enter note description..." :min-rows="3" />
        </div>

        <div v-if="note" class="form-row">
          <label class="form-label">Active</label>
          <SwitchInput v-model="isActive" />
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click">
        Cancel
      </button>
      <button class="button primary form-button" @click="handleSubmit" :disabled="isSubmitting">
        {{ isSubmitting ? 'Saving...' : (note ? 'Save Changes' : 'Create Note') }}
      </button>
    </div>
    <div v-if="error" class="modal-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import SelectInput from '@/components/inputs/SelectInput.vue';
import ProductSelectInput from '@/components/inputs/ProductSelectInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import SwitchInput from '@/components/inputs/SwitchInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as api from '@/api';

export default {
  name: 'PatientNoteModal',
  components: { SelectInput, ProductSelectInput, TextareaInput, SwitchInput, CloseIcon },
  props: {
    patientId: {
      type: Number,
      required: true
    },
    note: {
      type: Object,
      default: null
    }
  },
  emits: ['note-saved'],
  data() {
    return {
      type: 'misc',
      productId: null,
      description: '',
      isActive: true,
      isSubmitting: false,
      error: null,
      typeOptions: [
        { value: 'misc', label: 'Misc' },
        { value: 'product_log', label: 'Product Log' }
      ]
    };
  },
  created() {
    if (this.note) {
      this.type = this.note.type;
      this.productId = this.note.productId;
      this.description = this.note.description;
      this.isActive = this.note.isActive;
    }
  },
  methods: {
    async handleSubmit() {
      this.error = null;

      if (!this.description || this.description.trim().length === 0) {
        this.error = 'Description is required';
        return;
      }
      if (this.type === 'product_log' && !this.productId) {
        this.error = 'Product is required for Product Log notes';
        return;
      }

      this.isSubmitting = true;
      try {
        if (this.note) {
          await api.updatePatientNote(this.note.patientNoteId, {
            type: this.type,
            productId: this.type === 'product_log' ? this.productId : null,
            description: this.description.trim(),
            isActive: this.isActive
          });
        } else {
          await api.createPatientNote({
            patientId: this.patientId,
            type: this.type,
            productId: this.type === 'product_log' ? this.productId : null,
            description: this.description.trim()
          });
        }
        this.$emit('note-saved');
      } catch (err) {
        this.error = err.message;
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
